/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {GitHubOrgAndRepo} from './jotai';

import Link from './Link';
import URLFor from './URLFor';
import Username from './Username';
import {APP_HEADER_HEIGHT} from './constants';
import {primerColorModeAtom} from './jotai/atoms';
import {Box, Text, ToggleSwitch} from '@primer/react';
import {useAtom} from 'jotai';
import {useCallback} from 'react';

type Props = {
  orgAndRepo: GitHubOrgAndRepo | null;
};

export default function AppHeader({orgAndRepo}: Props): React.ReactElement {
  return (
    <Box
      sx={{
        fontSize: 2,
        height: APP_HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        bg: '#34495e',
        color: '#ecf0f1',
        borderBottom: '3px solid #2980b9',
      }}>
      <Box display="flex" alignItems="center" sx={{gap: 3}}>
        <Link href="/">
          <Text
            sx={{
              color: '#ecf0f1',
              fontWeight: 'bold',
              fontSize: '16px',
              letterSpacing: '0.5px',
              ':hover': {color: '#3498db'},
            }}>
            Differential
          </Text>
        </Link>
        {orgAndRepo != null && (
          <>
            <Text sx={{color: '#7f8c8d', fontSize: '14px'}}>·</Text>
            <PullsLink {...orgAndRepo} />
          </>
        )}
      </Box>
      <Box display="flex" alignItems="center" sx={{gap: 3}}>
        <ThemeSelector />
        <Username />
      </Box>
    </Box>
  );
}

function PullsLink({org, repo}: {org: string; repo: string}) {
  return (
    <Link href={URLFor.project({org, repo})}>
      <Text
        sx={{
          color: '#bdc3c7',
          fontWeight: 'normal',
          fontSize: '14px',
          ':hover': {color: '#ecf0f1'},
        }}>
        {org}
        {' / '}
        {repo}
      </Text>
    </Link>
  );
}

function ThemeSelector() {
  const [colorMode, setColorMode] = useAtom(primerColorModeAtom);
  const checked = colorMode === 'night';
  const onClick = useCallback(() => {
    setColorMode(colorMode === 'night' ? 'day' : 'night');
  }, [colorMode, setColorMode]);
  const sx = {'> [aria-hidden]': {display: 'none'}};
  return (
    <Text sx={{color: '#bdc3c7', fontSize: '13px'}}>
      <span id="theme-switch-label">Dark</span>:{' '}
      <ToggleSwitch
        checked={checked}
        onClick={onClick}
        size="small"
        aria-labelledby="theme-switch-label"
        sx={sx}
      />{' '}
    </Text>
  );
}
